import { useCallback, useMemo } from "react";

import { useTranslation } from "react-i18next";

import { MailIcon } from "@lucide/icons";

import type { IdeaWithCoverImage, OpenAPI } from "simplydo/interfaces";
import { ImageWithFallback } from "simplydo/react/components/ImageWithFallback";
import { Button } from "simplydo/react/ui/button";
import { Message } from "simplydo/react/ui/message";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "simplydo/react/ui/table";

import api from "api";
import util from "utils/utils";

import { UserChip } from "components/lib/Chips";
import BaseChip from "components/lib/Chips/BaseChip";
import type { ChipUser } from "components/lib/Chips/types";
import { AssessmentRoundLabel } from "components/lib/UI/AssessmentRoundLabel";

import type { LocalAssignments } from "./AssignmentDashboard";

type AssessmentReviewModalProps = {
  challenge: OpenAPI.GET<"/challenges/{id}">["response"];
  open: boolean;
  setOpen: (open: boolean) => void;
  localUserAssignments: LocalAssignments;
  localEmailAssignments: LocalAssignments;
  onSave: () => void;
};

export const AssessmentReviewModal = ({
  challenge,
  open,
  setOpen,
  localUserAssignments,
  localEmailAssignments,
  onSave,
}: AssessmentReviewModalProps) => {
  const { t } = useTranslation();
  const template = challenge?.ownerIdeaTemplate;
  const assessmentRoundMap = useMemo(() => {
    const assessmentRounds = template?.assessments?.rounds || [
      { id: "legacy", name: "Assessment" },
    ];
    return Object.fromEntries(assessmentRounds.map((r) => [r.id, r]));
  }, [template?.assessments?.rounds]);

  const localUserAssignmentList = useMemo(
    () => Object.entries(localUserAssignments),
    [localUserAssignments],
  );
  const localEmailAssignmentList = useMemo(
    () => Object.entries(localEmailAssignments),
    [localEmailAssignments],
  );

  // Saving the assessments requires transforming the local state into the api expected shape
  // The shapes are very similar but we cull the extra data such as the full user and idea objects
  const saveAssessments = useCallback(() => {
    type SlimAssignments = Record<
      string,
      { ideas: Record<string, { assessmentRoundIds: string[] }> }
    >;
    const userAssignments: SlimAssignments = {};
    for (const [userId, userAssignment] of localUserAssignmentList) {
      for (const [ideaId, ideaAssignment] of Object.entries(
        userAssignment.ideas,
      )) {
        if (!userAssignments[userId]) {
          userAssignments[userId] = { ideas: {} };
        }
        userAssignments[userId]!.ideas[ideaId] = {
          assessmentRoundIds: ideaAssignment.assessmentRoundIds,
        };
      }
    }
    const emailAssignments: SlimAssignments = {};
    for (const [email, emailAssignment] of localEmailAssignmentList) {
      for (const [ideaId, ideaAssignment] of Object.entries(
        emailAssignment.ideas,
      )) {
        if (!emailAssignments[email]) {
          emailAssignments[email] = { ideas: {} };
        }
        emailAssignments[email]!.ideas[ideaId] = {
          assessmentRoundIds: ideaAssignment.assessmentRoundIds,
        };
      }
    }
    api.challenges.assignAssessorAssignments(
      challenge?._id,
      { userAssignments, emailAssignments },
      () => {
        api.invalidateQueries(api.challenges.getAssessmentDashboard, [
          challenge?._id,
        ]);
        api.invalidateQueries(api.challenges.getIdeas, [challenge?._id]);
        api.invalidateQueries(api.challenges.getAssignedAssessments, [
          challenge?._id,
        ]);
        onSave();
        setOpen(false);
      },
      () => {},
    );
  }, [
    challenge?._id,
    localUserAssignmentList,
    localEmailAssignmentList,
    onSave,
    setOpen,
  ]);

  return (
    <Modal open={open} onClose={() => setOpen(false)}>
      <ModalHeader>Assignment review</ModalHeader>

      <ModalContent scrolling>
        <Message>
          Please review the assignments below. Once you confirm, the assessors
          will be notified and invited to assess the {t("generic.ideas")}.
        </Message>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHeaderCell collapsing>User</TableHeaderCell>
              <TableHeaderCell>Idea</TableHeaderCell>
              <TableHeaderCell collapsing>Assessment name</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {localUserAssignmentList.flatMap(([userId, assignment]) =>
              Object.values(assignment.ideas).flatMap((idea, i) =>
                idea.assessmentRoundIds.map((assessmentRoundId, j) => {
                  const round = assessmentRoundMap[assessmentRoundId];
                  return (
                    <TableRow key={userId}>
                      <TableCell>
                        {i === 0 && j === 0 ? (
                          <UserChip
                            user={assignment.user.ownerAssessor as ChipUser}
                            compact
                          />
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            gap: "0.5em",
                          }}
                        >
                          <ImageWithFallback
                            avatar
                            style={{
                              minWidth: 35,
                              width: 35,
                              height: 25,
                              borderRadius: 4,
                              objectFit: "cover",
                            }}
                            src={util.ideaCoverImage(
                              idea.idea as IdeaWithCoverImage,
                              "200x200",
                            )}
                            fallbackSrc={util.ideaCoverImage(undefined)}
                          />
                          {idea.idea.name}
                        </div>
                      </TableCell>
                      <TableCell>
                        {round && (
                          <AssessmentRoundLabel
                            key={round.id}
                            assessmentRound={round}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                }),
              ),
            )}
            {localEmailAssignmentList.flatMap(([userId, assignment]) =>
              Object.values(assignment.ideas).flatMap((idea, i) =>
                idea.assessmentRoundIds.map((assessmentRoundId, j) => {
                  const round = assessmentRoundMap[assessmentRoundId];
                  return (
                    <TableRow key={userId}>
                      <TableCell>
                        {i === 0 && j === 0 ? (
                          <BaseChip
                            icon={MailIcon}
                            title={assignment.user.email}
                            compact="very"
                            content={`This is a pending invitation, the user has not yet signed up to ${util.appName()}.`}
                          />
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            gap: "0.5em",
                          }}
                        >
                          <ImageWithFallback
                            avatar
                            style={{
                              minWidth: 35,
                              width: 35,
                              height: 25,
                              borderRadius: 4,
                              objectFit: "cover",
                            }}
                            src={util.ideaCoverImage(
                              idea.idea as IdeaWithCoverImage,
                              "200x200",
                            )}
                            fallbackSrc={util.ideaCoverImage(undefined)}
                          />
                          {idea.idea.name}
                        </div>
                      </TableCell>
                      <TableCell>
                        {round && (
                          <AssessmentRoundLabel
                            key={round.id}
                            assessmentRound={round}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                }),
              ),
            )}
          </TableBody>
        </Table>
      </ModalContent>
      <ModalActions>
        <Button onClick={() => setOpen(false)}>Cancel</Button>
        <Button onClick={saveAssessments} color="primary">
          Save assignments
        </Button>
      </ModalActions>
    </Modal>
  );
};
